/**
 * 来場者向け 投票フェス結果モーダル
 * 結果表示 ON の際に来場者ホームで一度だけ表示される投票結果画面
 * VotingFestModal と同じフェス風スタイルで 1 位のキャラクターだけを表示します
 * 各候補の票数・得票率・総票数は来場者に公開しません
 */

import React from 'react';
import { Image, Modal, Pressable, ScrollView, Text, View } from '../../../shared/components/WebPrimitives';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import FestivalOrnamentStrip from './FestivalOrnamentStrip';

/** キャラごとのテーマカラー（VotingFestModal と共通） */
const OPTION_THEME = Object.freeze({
  a: { header: '#B91C1C' },
  b: { header: '#1E3A8A' },
  c: { header: '#14532D' },
});

/**
 * 来場者向け 投票フェス結果モーダル
 * @param {Object} props - プロパティ
 * @param {boolean} props.visible - 表示可否
 * @param {string} props.questionText - 質問テキスト
 * @param {Array<{ key: 'a' | 'b' | 'c', label: string, description: string, imageUrl: string }>} props.options - 投票オプション
 * @param {'a' | 'b' | 'c' | null} props.winningOption - 1 位候補キー（未確定なら null）
 * @param {() => void} props.onClose - 閉じるコールバック
 * @returns {JSX.Element} 投票結果モーダル
 */
const VotingFestResultsModal = ({
  visible,
  questionText,
  options,
  winningOption,
  onClose,
}) => {
  /** 公開アプリ設定 */
  const { settings } = usePublicAppSettings();

  /** 1 位候補のオプション（未確定なら undefined） */
  const winner = winningOption ? options.find((o) => o.key === winningOption) : undefined;

  /** 1 位候補のテーマカラー */
  const winnerTheme = OPTION_THEME[winningOption] ?? OPTION_THEME.a;

  /** 1 位候補の表示名 */
  const winnerLabel = winner?.label || (winningOption ? `キャラ${winningOption.toUpperCase()}` : '');

  return (
    <Modal
      accessibilityLabel="企画投票の結果"
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
      transparent
      visible={visible}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.90)',
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: 8,
          paddingVertical: 12,
        }}
      >
        {/* コーナースター装飾 */}
        {[
          { top: 16, left: 10 }, { top: 16, right: 10 },
          { bottom: 20, left: 10 }, { bottom: 20, right: 10 },
        ].map((pos, i) => (
          <View key={`deco-${i}`} pointerEvents="none" style={{ position: 'absolute', ...pos }}>
            <Text style={{ color: '#FFD700', fontSize: 20, opacity: 0.65 }}>{i % 2 === 0 ? '★' : '✦'}</Text>
          </View>
        ))}

        {/* メインパネル */}
        <View
          testID="voting-results-dialog-panel"
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100dvh - 24px)',
            minHeight: 0,
            width: '100%',
            maxWidth: 480,
            borderWidth: 4,
            borderColor: '#1A2D4D',
            overflow: 'hidden',
            shadowColor: '#FFD700',
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.85,
            shadowRadius: 22,
            elevation: 28,
          }}
        >
          {/* 上部装飾帯 */}
          <FestivalOrnamentStrip />

          {/* フェスヘッダー */}
          <View
            style={{
              backgroundColor: '#130B2E',
              borderBottomWidth: 3,
              borderBottomColor: '#1A2D4D',
              flexShrink: 0,
              paddingHorizontal: 12,
              paddingTop: 10,
              paddingBottom: 8,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={{ flex: 1, height: 1, backgroundColor: '#FFD700', opacity: 0.45 }} />
              <Text style={{ color: '#FFD700', fontSize: 10, fontWeight: '900', letterSpacing: 2.5 }}>
                🎪 {settings.festivalLabelEn} 🎪
              </Text>
              <View style={{ flex: 1, height: 1, backgroundColor: '#FFD700', opacity: 0.45 }} />
            </View>

            {/* 結果発表バナー */}
            <View
              style={{
                marginTop: 8,
                backgroundColor: '#FFD700',
                borderWidth: 2,
                borderColor: '#1A2D4D',
                paddingVertical: 7,
                paddingHorizontal: 10,
              }}
            >
              <Text
                style={{ color: '#130B2E', fontSize: 15, fontWeight: '900', textAlign: 'center', letterSpacing: 0.3 }}
              >
                🏆 投票結果発表！ 🏆
              </Text>
            </View>
          </View>

          {/* コンテンツエリア */}
          <ScrollView
            contentContainerStyle={{ padding: 10, gap: 8 }}
            style={{ backgroundColor: '#FFFCE8', flex: 1, minHeight: 0 }}
            testID="voting-results-dialog-content"
          >
            {/* 質問文 */}
            <View
              style={{
                backgroundColor: '#FFF4C2',
                borderWidth: 2,
                borderColor: '#1A2D4D',
                borderStyle: 'dashed',
                paddingVertical: 6,
                paddingHorizontal: 10,
              }}
            >
              <Text style={{ color: '#1A2D4D', fontSize: 12, fontWeight: '900', textAlign: 'center' }}>
                {questionText || `${settings.festivalName}のマスコットキャラクターを選ぼう！`}
              </Text>
            </View>

            {/* 1 位のキャラクター */}
            {winner ? (
              <View
                testID="voting-results-winner"
                style={{
                  borderWidth: 3,
                  borderColor: '#FFD700',
                  overflow: 'hidden',
                  shadowColor: '#FFD700',
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0.85,
                  shadowRadius: 14,
                  elevation: 12,
                }}
              >
                {/* カードヘッダー */}
                <View
                  style={{
                    backgroundColor: winnerTheme.header,
                    paddingVertical: 6,
                    paddingHorizontal: 10,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{ color: '#FFD700', fontSize: 14, fontWeight: '900', letterSpacing: 1 }}>
                    🥇 第1位 🥇
                  </Text>
                </View>

                {/* カード本体 */}
                <View style={{ alignItems: 'center', backgroundColor: '#FFFDF0', padding: 12, gap: 8 }}>
                  {/* キャラ画像 */}
                  <View
                    style={{
                      borderWidth: 2,
                      borderColor: winnerTheme.header,
                      backgroundColor: '#FFF8DD',
                      padding: 6,
                    }}
                  >
                    {winner.imageUrl ? (
                      <Image
                        accessibilityLabel={`${winnerLabel}の画像`}
                        decoding="async"
                        height={160}
                        loading="lazy"
                        resizeMode="contain"
                        source={{ uri: winner.imageUrl }}
                        style={{ width: 160, height: 160 }}
                        width={160}
                      />
                    ) : (
                      <View style={{ width: 160, height: 160, alignItems: 'center', justifyContent: 'center' }}>
                        <Text style={{ fontSize: 64 }}>🎭</Text>
                      </View>
                    )}
                  </View>

                  {/* キャラ名 */}
                  <Text style={{ color: '#1A2D4D', fontSize: 18, fontWeight: '900', textAlign: 'center' }}>
                    {winnerLabel}
                  </Text>

                  {/* 説明文 */}
                  {winner.description ? (
                    <Text style={{ color: '#344766', fontSize: 12, lineHeight: 18, textAlign: 'center' }}>
                      {winner.description}
                    </Text>
                  ) : null}
                </View>
              </View>
            ) : (
              <View
                testID="voting-results-pending"
                style={{
                  backgroundColor: '#FFFDF0',
                  borderWidth: 2,
                  borderColor: '#1A2D4D',
                  paddingVertical: 16,
                  paddingHorizontal: 12,
                }}
              >
                <Text style={{ color: '#1A2D4D', fontSize: 13, fontWeight: '900', textAlign: 'center' }}>
                  結果は集計中です。発表までお待ちください。
                </Text>
              </View>
            )}

            {/* ありがとうメッセージ */}
            <View
              style={{
                backgroundColor: '#FFF4C2',
                borderWidth: 2,
                borderColor: '#FFD700',
                paddingVertical: 10,
                paddingHorizontal: 12,
                marginTop: 4,
              }}
            >
              <Text style={{ color: '#1A2D4D', fontSize: 12, fontWeight: '900', textAlign: 'center' }}>
                ★ ご参加ありがとうございました！ ★
              </Text>
            </View>

          </ScrollView>

          {/* 固定操作部 */}
          <View
            testID="voting-results-dialog-actions"
            style={{
              backgroundColor: '#FFFCE8',
              borderTopColor: '#1A2D4D',
              borderTopWidth: 2,
              flexShrink: 0,
              paddingHorizontal: 10,
              paddingVertical: 8,
            }}
          >
            <Pressable
              accessibilityRole="button"
              className="active:bg-[#0F1A2E]"
              onPress={onClose}
              style={{
                alignItems: 'center',
                backgroundColor: '#1A2D4D',
                borderColor: '#FFD700',
                borderWidth: 3,
                justifyContent: 'center',
                minHeight: 48,
                paddingHorizontal: 8,
                paddingVertical: 8,
              }}
            >
              <Text style={{ color: '#FFD700', fontSize: 15, fontWeight: '900', letterSpacing: 0.5 }}>
                ✦ 結果を閉じる ✦
              </Text>
            </Pressable>
          </View>

          {/* 下部装飾帯 */}
          <FestivalOrnamentStrip />
        </View>
      </View>
    </Modal>
  );
};

export default VotingFestResultsModal;
